/**
 * Resolves the full set of fields needed for an import/edition given a user selection.
 *
 * Starting from the user's picked fields it expands the set to include:
 * 1. Identifier fields (always required)
 * 2. Fields referenced by `required_with` / `required_with_all` validations
 *    of any selected field
 * 3. Fields whose `required_with` / `required_with_all` validations reference
 *    an already-selected field (reverse dependency)
 *
 * The output distinguishes between user-chosen fields and auto-added dependency
 * fields so the UI can communicate what was added and why.
 */

export interface SchemaField {
  key: string
  identifier?: boolean
  hidden?: boolean
  leDependant?: boolean
  validations?: Array<{
    validate?: string | null
    columns?: string[] | null
  }> | null
}

export interface ResolvedDependencies {
  /** The full expanded set of field keys (user selection + dependencies) */
  expandedSelection: string[]
  /** Fields that were in the original user selection */
  requiredFields: string[]
  /** Fields that were auto-added as dependencies (not in original selection) */
  dependencyFields: string[]
}

export function resolveRequiredDependencies(
  selectedKeys: string[],
  schema: SchemaField[]
): ResolvedDependencies {
  const schemaByKey = new Map(schema.map((f) => [f.key, f]))
  const selectedSet = new Set(selectedKeys)
  const expanded = new Set(selectedKeys)

  // 1. Always include identifier fields
  for (const field of schema) {
    if (field.identifier) {
      expanded.add(field.key)
    }
  }

  // 2. For each selected field, check if its validations reference other fields
  for (const key of selectedKeys) {
    const field = schemaByKey.get(key)
    if (!field?.validations) continue

    for (const validation of field.validations) {
      if (
        (validation.validate === 'required_with' || validation.validate === 'required_with_all') &&
        validation.columns
      ) {
        for (const col of validation.columns) {
          expanded.add(col)
        }
      }
    }
  }

  // 3. Check if any non-selected field has a required_with pointing to a selected field
  //    — if so, that field might need to be included too
  for (const field of schema) {
    if (expanded.has(field.key)) continue
    if (!field.validations) continue

    for (const validation of field.validations) {
      if (
        (validation.validate === 'required_with' || validation.validate === 'required_with_all') &&
        validation.columns?.some((col) => expanded.has(col))
      ) {
        expanded.add(field.key)
        break
      }
    }
  }

  const expandedSelection = Array.from(expanded)
  const dependencyFields = expandedSelection.filter((key) => !selectedSet.has(key))

  return {
    expandedSelection,
    requiredFields: selectedKeys,
    dependencyFields,
  }
}
